/**
 * Admin Orca Alerts RPC definitions
 *
 * Operator-facing endpoints for the Twilio SMS ops-alert channel that backs the
 * `/orca/defaults` "SMS alerts" card:
 *
 *   - `status`: is SMS configured, is the kill switch on, the masked from-number,
 *     the masked recipients, and the automatic-alert cooldown. Never leaks a full
 *     phone number, a Twilio SID, or any auth value.
 *   - `sendTest`: send a test SMS to every configured recipient right now,
 *     bypassing the cooldown, so an operator can prove the Twilio setup
 *     end-to-end before enabling automatic alerts.
 *
 * @since 1.0.0
 */
import { Rpc, RpcGroup } from "@effect/rpc";
import { Schema } from "effect";
import { AdminMiddleware } from "../admin.tags.ts";
import { Forbidden, SmsNotificationError, Unauthorized } from "../errors.ts";

// ============================================================================
// Response Schemas
// ============================================================================

/**
 * Operator-facing status of the SMS ops-alert channel. `fromNumber` and every
 * entry of `recipients` are masked (all but the last four characters replaced by
 * `•`, preserving length) so the card can render them without exposing a full
 * phone number. `cooldownSeconds` is the per-dedupe-key cooldown that governs
 * automatic alerts only (never the explicit test send).
 *
 * @since 1.0.0
 */
export const OrcaSmsAlertStatus = Schema.Struct({
  configured: Schema.Boolean,
  disabled: Schema.Boolean,
  fromNumber: Schema.NullOr(Schema.String),
  recipients: Schema.Array(Schema.String),
  cooldownSeconds: Schema.Number,
});

/**
 * @since 1.0.0
 */
export type OrcaSmsAlertStatus = Schema.Schema.Type<typeof OrcaSmsAlertStatus>;

/**
 * Result of a test send: how many recipients were reached and the Twilio message
 * SIDs, in send order. No auth token or full phone number is ever returned.
 *
 * @since 1.0.0
 */
export const OrcaSmsTestSendResult = Schema.Struct({
  sent: Schema.Number,
  sids: Schema.Array(Schema.String),
});

/**
 * @since 1.0.0
 */
export type OrcaSmsTestSendResult = Schema.Schema.Type<typeof OrcaSmsTestSendResult>;

// ============================================================================
// RPC Group
// ============================================================================

/**
 * Admin Orca Alerts RPC group. Both endpoints require admin role via
 * `AdminMiddleware`; the tags are the full dot-namespaced names.
 *
 * @since 1.0.0
 */
export class AdminOrcaAlertRpcs extends RpcGroup.make(
  // Read-only channel status: never touches Twilio, so it cannot fail on a
  // Twilio error — only the middleware's auth errors.
  Rpc.make("admin.orcaAlerts.status", {
    success: OrcaSmsAlertStatus,
    error: Schema.Union(Unauthorized, Forbidden),
  }).middleware(AdminMiddleware),
  // Explicit operator test send: bypasses the cooldown and ignores the kill
  // switch (the kill switch governs automatic alerts only). Fails with a typed
  // SmsNotificationError when SMS is unconfigured or Twilio rejects a message.
  Rpc.make("admin.orcaAlerts.sendTest", {
    success: OrcaSmsTestSendResult,
    error: Schema.Union(Unauthorized, Forbidden, SmsNotificationError),
  }).middleware(AdminMiddleware),
) {}
